import React, { ReactNode, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Shield, UserRound, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { authApi } from '../services/api';
import { BottomNav } from './navigation/BottomNav';
import { MoreMenu } from './navigation/MoreMenu';
import { getVisibleNavigationItems, splitDesktopNavigation, splitMobileNavigation } from './navigation/navigation';
import NotificationBell from './NotificationBell';
import CheckInButton from './checkIn/CheckInButton';
import ProfileDialog from './account/ProfileDialog';
import { Button, Input, Modal, useToast } from './ui';

interface LayoutProps {
    children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
    const { user, logout } = useAuth();
    const { hasAnyPermission } = usePermissions();
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [adminMenuOpen, setAdminMenuOpen] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const visibleItems = getVisibleNavigationItems(hasAnyPermission, user?.role);
    const mobileNavigation = splitMobileNavigation(visibleItems);
    const desktopNavigation = splitDesktopNavigation(visibleItems);
    const currentAdminItem = desktopNavigation.admin.find(
        (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
    );
    const roleLabel =
        {
            ADMIN: 'Quản trị viên',
            MANAGER: 'Quản lý',
            TEACHER: 'Giáo viên',
            ASSISTANT: 'Trợ giảng',
            STAFF: 'Nhân viên',
        }[user?.role || 'STAFF'] || user?.role;
    const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}` || 'A';

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handlePasswordChange = async (event: React.FormEvent) => {
        event.preventDefault();

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showToast('Mật khẩu xác nhận không khớp.', 'error');
            return;
        }
        if (passwordForm.newPassword.length < 6) {
            showToast('Mật khẩu mới phải có ít nhất 6 ký tự.', 'error');
            return;
        }

        setIsChangingPassword(true);
        try {
            await authApi.changePassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
            });
            showToast('Đổi mật khẩu thành công.', 'success');
            setShowPasswordModal(false);
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error: any) {
            showToast(error?.message || 'Đổi mật khẩu thất bại.', 'error');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleClosePasswordModal = React.useCallback(() => {
        if (!isChangingPassword) setShowPasswordModal(false);
    }, [isChangingPassword]);

    return (
        <div className="min-h-screen text-ink-900">
            <header className="sticky top-0 z-40 border-b border-ink-200 bg-white">
                <div className="flex h-14 items-center gap-6 px-3 sm:px-5 lg:px-7">
                    <NavLink to="/dashboard" className="flex shrink-0 items-center gap-2.5">
                        <img
                            src="/athena-logo.jpg"
                            alt=""
                            className="h-7 w-7 rounded object-cover"
                        />
                        <span className="text-[15px] font-semibold text-ink-900">Athena</span>
                    </NavLink>

                    <nav className="hidden h-full min-w-0 items-stretch gap-5 lg:flex" aria-label="Điều hướng chính">
                        <div className="flex min-w-0 items-stretch gap-5 overflow-x-auto">
                            {desktopNavigation.primary.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) =>
                                        `flex items-center whitespace-nowrap border-b-2 text-sm transition-colors ${
                                            isActive
                                                ? 'border-academy-900 font-semibold text-ink-900'
                                                : 'border-transparent text-ink-600 hover:text-ink-900'
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </div>
                        {desktopNavigation.admin.length > 0 ? (
                            <div className="relative flex items-stretch">
                                <button
                                    type="button"
                                    onClick={() => setAdminMenuOpen((open) => !open)}
                                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap border-b-2 text-sm transition-colors ${
                                        currentAdminItem
                                            ? 'border-academy-900 font-semibold text-ink-900'
                                            : 'border-transparent text-ink-600 hover:text-ink-900'
                                    }`}
                                    aria-expanded={adminMenuOpen}
                                    aria-haspopup="menu"
                                >
                                    {currentAdminItem?.label || 'Quản trị'}
                                    <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                                {adminMenuOpen && (
                                    <div
                                        role="menu"
                                        className="absolute left-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-lg border border-ink-200 bg-white py-1 shadow-raised"
                                    >
                                        {desktopNavigation.admin.map((item) => (
                                            <NavLink
                                                key={item.path}
                                                to={item.path}
                                                role="menuitem"
                                                onClick={() => setAdminMenuOpen(false)}
                                                className={({ isActive }) =>
                                                    `block px-3 py-2 text-sm transition-colors hover:bg-ink-100 ${
                                                        isActive ? 'font-semibold text-ink-900' : 'text-ink-700'
                                                    }`
                                                }
                                            >
                                                {item.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </nav>

                    <div className="ml-auto flex shrink-0 items-center gap-2">
                        <CheckInButton />
                        <NotificationBell />
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setAccountMenuOpen((open) => !open)}
                                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-ink-200 text-xs font-semibold text-ink-900 transition-colors hover:bg-ink-300"
                                aria-label={`Tài khoản: ${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                                aria-expanded={accountMenuOpen}
                                aria-haspopup="menu"
                            >
                                {initials}
                            </button>

                            {accountMenuOpen && (
                                <div
                                    role="menu"
                                    className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-lg border border-ink-200 bg-white py-1 shadow-raised"
                                >
                                    <div className="border-b border-ink-200 px-3 py-2.5">
                                        <p className="truncate text-sm font-medium text-ink-900">
                                            {user?.firstName} {user?.lastName}
                                        </p>
                                        <p className="truncate text-xs text-ink-500">{roleLabel}</p>
                                    </div>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setShowProfile(true);
                                            setAccountMenuOpen(false);
                                        }}
                                        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                    >
                                        <UserRound className="h-4 w-4 text-ink-500" />
                                        Thông tin cá nhân
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setShowPasswordModal(true);
                                            setAccountMenuOpen(false);
                                        }}
                                        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                    >
                                        <Shield className="h-4 w-4 text-ink-500" />
                                        Đổi mật khẩu
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            navigate('/my-payroll');
                                            setAccountMenuOpen(false);
                                        }}
                                        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                    >
                                        <Wallet className="h-4 w-4 text-ink-500" />
                                        Bảng công của tôi
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={handleLogout}
                                        className="flex w-full cursor-pointer items-center gap-3 border-t border-ink-200 px-3 py-2 text-left text-sm text-danger-600 transition-colors hover:bg-danger-50"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Đăng xuất
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full min-w-0 max-w-[1600px] px-3 pb-24 pt-4 sm:px-5 lg:px-7 lg:pb-10 lg:pt-6">
                {children}
            </main>

            {(accountMenuOpen || adminMenuOpen) && (
                <button
                    type="button"
                    className="fixed inset-0 z-30 cursor-default"
                    onClick={() => {
                        setAccountMenuOpen(false);
                        setAdminMenuOpen(false);
                    }}
                    aria-label="Đóng menu"
                />
            )}

            <BottomNav
                items={mobileNavigation.primary}
                hasMore={mobileNavigation.secondary.length > 0}
                onMore={() => setMoreMenuOpen(true)}
            />
            <MoreMenu
                open={moreMenuOpen}
                items={mobileNavigation.secondary}
                onClose={() => setMoreMenuOpen(false)}
            />

            <ProfileDialog open={showProfile} onClose={() => setShowProfile(false)} />

            <Modal
                open={showPasswordModal}
                title="Đổi mật khẩu"
                description="Cập nhật mật khẩu đăng nhập cho tài khoản hiện tại."
                onClose={handleClosePasswordModal}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={handleClosePasswordModal} disabled={isChangingPassword}>
                            Hủy
                        </Button>
                        <Button type="submit" form="change-password-form" variant="primary" loading={isChangingPassword}>
                            Cập nhật
                        </Button>
                    </div>
                }
            >
                <form id="change-password-form" onSubmit={handlePasswordChange} className="space-y-4">
                    <Input
                        label="Mật khẩu hiện tại"
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
                        required
                        disabled={isChangingPassword}
                    />
                    <Input
                        label="Mật khẩu mới"
                        hint="Mật khẩu phải có ít nhất 6 ký tự"
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                        required
                        minLength={6}
                        disabled={isChangingPassword}
                    />
                    <Input
                        label="Xác nhận mật khẩu mới"
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                        required
                        disabled={isChangingPassword}
                    />
                </form>
            </Modal>
        </div>
    );
};

export default Layout;
